import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import api from 'api';
import { User } from '../../../../../../../../../../../models/User';
import { Link } from 'react-router-dom';
import useCallbackAsync from 'hooks/useCallbackAsync';
import {
  getEditUserRoute,
  getIsArrayEmpty,
  isEnterPressed,
  renderFullUserInfo,
} from 'utils';
import NoUsersFound from '../NoUsersFound';

import './SearchUser.styles.scss';

const LIMIT = '20';

type SearchUserProps = {
  className?: string;
};

const SearchUser: React.FC<SearchUserProps> = (props) => {
  const { className } = props;

  const [value, setValue] = useState('');
  const [users, setUsers] = useState<User[]>([]);

  const hasFetched = useRef(false);

  const { t } = useTranslation();

  const classes = classNames('search-user', className);

  const [onSearch, isLoading] = useCallbackAsync(async () => {
    const {
      data: { items },
    } = await api.portfolio.admin.getAllUsers({
      $searchString: value,
      $limit: LIMIT,
    });
    setUsers(items);
    hasFetched.current = true;
  });

  const canSearch = useMemo(() => !isLoading && !!value, [isLoading, value]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!canSearch) return;

      if (isEnterPressed(e)) onSearch();
    },
    [canSearch, onSearch],
  );

  return (
    <div className={classes}>
      <p className="search-user__title">{t('manageUserProfile')}</p>
      <p className="search-user__description">
        {t('manageUserProfileDescription')}
      </p>

      <div className="search-user__search">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button disabled={!canSearch} onClick={onSearch}>
          {t('search')}
        </Button>
      </div>

      <div className="search-user__list">
        {hasFetched.current && getIsArrayEmpty(users) && !isLoading && (
          <NoUsersFound />
        )}

        {!isLoading &&
          users.map((user) => (
            <Link key={user._id} to={getEditUserRoute(user._id)}>
              {renderFullUserInfo(user)}
            </Link>
          ))}
      </div>
    </div>
  );
};

export default SearchUser;
